import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import {
  listSupplyItemsFn,
  listSupplyVendorsFn,
  receiveSupplyStockFn,
} from "@/supplies/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_supplies-layout/supplies/receive")({
  component: ReceivePage,
})

interface ItemOption {
  id: number
  name: string
  unit: string
  quantityOnHand: number
  isActive: boolean
}

interface VendorOption {
  id: number
  name: string
  isActive: boolean
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function parseUnitCostCents(usd: string): number | null {
  const trimmed = usd.trim()
  if (!trimmed) return null
  const dollars = Number.parseFloat(trimmed)
  if (!Number.isFinite(dollars) || dollars < 0) {
    throw new Error("Unit cost must be a non-negative number")
  }
  return Math.round(dollars * 100)
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function ReceivePage() {
  const queryClient = useQueryClient()
  const [itemId, setItemId] = useState("")
  const [quantity, setQuantity] = useState("")
  const [vendorId, setVendorId] = useState("")
  const [invoiceNumber, setInvoiceNumber] = useState("")
  const [unitCostUsd, setUnitCostUsd] = useState("")
  const [note, setNote] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const itemsQuery = useQuery({
    queryKey: ["supply-items", { isActive: true, pageSize: 200 }],
    queryFn: () =>
      listSupplyItemsFn({
        data: { isActive: true, pageSize: 200 },
      }),
  })

  const vendorsQuery = useQuery({
    queryKey: ["supply-vendors", { includeInactive: false }],
    queryFn: () => listSupplyVendorsFn({ data: { includeInactive: false } }),
  })

  const receive = useMutation({
    mutationFn: (input: {
      itemId: number
      quantity: number
      vendorId: number | null
      invoiceNumber: string | null
      unitCostCents: number | null
      note: string | null
    }) => receiveSupplyStockFn({ data: input }),
    onSuccess: async (result) => {
      setFormError(null)
      const item = result.item
      setSuccess(
        `Received ${result.movement.quantityDelta} ${item.unit} of “${item.name}”. On hand: ${item.quantityOnHand}.`,
      )
      setQuantity("")
      setInvoiceNumber("")
      setUnitCostUsd("")
      setNote("")
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
      await queryClient.invalidateQueries({ queryKey: ["supply-movements"] })
      await queryClient.invalidateQueries({ queryKey: ["supplies-dashboard"] })
    },
    onError: (error) => {
      setSuccess(null)
      setFormError(getErrorMessage(error))
    },
  })

  const items = (itemsQuery.data?.items ?? []) as ItemOption[]
  const vendors = (vendorsQuery.data ?? []) as VendorOption[]
  const selectedItem = items.find((item) => String(item.id) === itemId)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (receive.isPending) return
    setFormError(null)
    setSuccess(null)

    try {
      const parsedItemId = Number(itemId)
      if (!Number.isInteger(parsedItemId) || parsedItemId <= 0) {
        setFormError("Select an item")
        return
      }
      const qty = Number(quantity)
      if (!Number.isInteger(qty) || qty <= 0) {
        setFormError("Quantity must be a positive integer")
        return
      }
      const parsedVendorId = vendorId ? Number(vendorId) : null
      if (
        vendorId &&
        (!Number.isInteger(parsedVendorId) || (parsedVendorId ?? 0) <= 0)
      ) {
        setFormError("Invalid vendor")
        return
      }

      await receive.mutateAsync({
        itemId: parsedItemId,
        quantity: qty,
        vendorId: parsedVendorId,
        invoiceNumber: invoiceNumber.trim() || null,
        unitCostCents: parseUnitCostCents(unitCostUsd),
        note: note.trim() || null,
      })
    } catch (error) {
      if (!(error instanceof Error && receive.isError)) {
        setFormError(getErrorMessage(error))
      }
    }
  }

  const listError = itemsQuery.isError
    ? getErrorMessage(itemsQuery.error)
    : vendorsQuery.isError
      ? getErrorMessage(vendorsQuery.error)
      : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Receive</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Add stock to a supply item and record a receive movement.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Receive stock</CardTitle>
          <CardDescription>
            Quantity must be positive. Vendor, invoice, cost, and note are
            optional.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-4"
          >
            <div className="grid gap-1.5">
              <Label htmlFor="receive-item">Item</Label>
              <select
                id="receive-item"
                value={itemId}
                onChange={(event) => setItemId(event.target.value)}
                disabled={receive.isPending || itemsQuery.isLoading}
                required
                className={selectClassName()}
              >
                <option value="">Select item…</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({item.quantityOnHand} {item.unit} on hand)
                  </option>
                ))}
              </select>
              {selectedItem ? (
                <p className="text-muted-foreground text-xs">
                  Current on hand:{" "}
                  <span className="font-medium tabular-nums">
                    {selectedItem.quantityOnHand}
                  </span>{" "}
                  {selectedItem.unit}
                  {" · "}
                  <Link
                    to="/supplies/items/$itemId"
                    params={{ itemId: String(selectedItem.id) }}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    View item
                  </Link>
                </p>
              ) : null}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="receive-quantity">Quantity</Label>
              <Input
                id="receive-quantity"
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                disabled={receive.isPending}
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="receive-vendor">Vendor</Label>
              <select
                id="receive-vendor"
                value={vendorId}
                onChange={(event) => setVendorId(event.target.value)}
                disabled={receive.isPending || vendorsQuery.isLoading}
                className={selectClassName()}
              >
                <option value="">None</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="receive-invoice">Invoice number</Label>
              <Input
                id="receive-invoice"
                value={invoiceNumber}
                onChange={(event) => setInvoiceNumber(event.target.value)}
                disabled={receive.isPending}
                autoComplete="off"
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="receive-unit-cost">Unit cost (USD)</Label>
              <Input
                id="receive-unit-cost"
                type="number"
                min={0}
                step="0.01"
                value={unitCostUsd}
                onChange={(event) => setUnitCostUsd(event.target.value)}
                disabled={receive.isPending}
                placeholder="0.00"
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="receive-note">Note</Label>
              <Input
                id="receive-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                disabled={receive.isPending}
                autoComplete="off"
              />
            </div>

            {listError ? (
              <p className="text-destructive text-sm" role="alert">
                {listError}
              </p>
            ) : null}
            {formError ? (
              <p className="text-destructive text-sm" role="alert">
                {formError}
              </p>
            ) : null}
            {success ? (
              <output className="block text-green-700 text-sm dark:text-green-400">
                {success}
              </output>
            ) : null}

            <div className="flex justify-end">
              <Button type="submit" disabled={receive.isPending}>
                {receive.isPending ? "Receiving…" : "Receive stock"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
